import { selectRow } from '@client-shared/ui-agGrid';
import {
  FETCH_CUSTOM_GROUPS,
  FETCH_CUSTOM_GROUPS_SUCCESS,
  FETCH_CUSTOM_GROUPS_FAILURE,
  FETCH_CUSTOM_GROUP_ITEMS,
  FETCH_CUSTOM_GROUP_ITEMS_SUCCESS,
  FETCH_CUSTOM_GROUP_ITEMS_FAILURE,
  CREATE_CUSTOM_GROUP,
  CLEAR_CREATED_CUSTOM_GROUP,
  SAVE_CUSTOM_GROUP,
  SAVE_CUSTOM_GROUP_SUCCESS,
  SAVE_CUSTOM_GROUP_FAILURE,
  EDIT_CUSTOM_GROUP,
  EDIT_CUSTOM_GROUP_SUCCESS,
  EDIT_CUSTOM_GROUP_FAILURE,
  DELETE_CUSTOM_GROUP,
  DELETE_CUSTOM_GROUP_SUCCESS,
  DELETE_CUSTOM_GROUP_FAILURE,
  FETCH_CUSTOM_GROUP_ITEM_TYPES,
  FETCH_CUSTOM_GROUP_ITEM_TYPES_SUCCESS,
  FETCH_CUSTOM_GROUP_ITEM_TYPES_FAILURE,
  ENTER_CUSTOM_GROUP_EDIT_MODE,
  EXIT_CUSTOM_GROUP_EDIT_MODE,
  SELECT_CUSTOM_GROUP,
  SELECT_CUSTOM_GROUP_ITEM_TYPE,
  REFRESH_CUSTOM_GROUP,
  REFRESH_CUSTOM_GROUP_FAILURE,
  REFRESH_CUSTOM_GROUP_SUCCESS,
  FETCH_USER_PRIVILEGES,
  FETCH_USER_PRIVILEGES_SUCCESS,
  FETCH_USER_PRIVILEGES_FAILURE,
  IS_ARCHIVE_ADMIN,
} from './types';

import {
  fetchUserPrivileges as fetchUserPrivilegesApi,
  fetchCustomGroups as fetchCustomGroupsApi,
  editCustomGroup as editCustomGroupApi,
  saveNewCustomGroup as saveNewCustomGroupApi,
  deleteCustomGroup as deleteCustomGroupApi,
  fetchGroupItemTypes as fetchGroupItemTypesApi,
  fetchGroupItems as fetchGroupItemsApi,
  refreshCustomGroupData as refreshCustomGroupDataApi,
} from '../api';
import { fetchingError } from '../../../shared/store/actions';
import { errorNotification, successNotification } from '../../../shared/store/notificationActions';

import getModalActions from '../../../shared/modals/getModalActions';

import ModalNames from '../modalNames';

import agGridConfig from '../../../agGridConfig';
import { CUSTOM_GROUP_TYPES } from '../constants';

export const {
  showAction: showDiscardChangesConfirmModal,
  hideAction: hideDiscardChangesConfirmModal,
} = getModalActions(ModalNames.confirmDiscardCustomGroupChanges);

export const {
  showAction: showDeleteCustomGroupModal,
  hideAction: hideDeleteCustomGroupModal,
} = getModalActions(ModalNames.confirmDeletingCustomGroup);

export const selectCustomGroup = selectedCustomGroupId => async dispatch => dispatch({
  type: SELECT_CUSTOM_GROUP,
  selectedCustomGroupId,
});

export const selectCustomGroupItemType = selectedCustomGroupItemType => async dispatch => dispatch({
  type: SELECT_CUSTOM_GROUP_ITEM_TYPE,
  selectedCustomGroupItemType,
});

export const isArchiveAdmin = isArchiveAdminFlag => async dispatch => dispatch({
  type: IS_ARCHIVE_ADMIN,
  isArchiveAdminFlag,
});

export const fetchUserPrivileges = userPrivilege => async dispatch => {
  dispatch({ type: FETCH_USER_PRIVILEGES });

  try {
    const isArchiveAdminFlag = await fetchUserPrivilegesApi(userPrivilege);
    if (isArchiveAdminFlag) {
      await dispatch({
        type: FETCH_USER_PRIVILEGES_SUCCESS,
        isArchiveAdminFlag,
      });
      dispatch(isArchiveAdmin(isArchiveAdminFlag));
    }
  } catch (error) {
    dispatch({ type: FETCH_USER_PRIVILEGES_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const fetchCustomGroups = ({ selectFirstItem } = {}) => async dispatch => {
  dispatch({ type: FETCH_CUSTOM_GROUPS });

  try {
    const customGroups = await fetchCustomGroupsApi();

    await dispatch({
      type: FETCH_CUSTOM_GROUPS_SUCCESS,
      customGroups,
    });

    if (selectFirstItem && customGroups.length) {
      dispatch(selectCustomGroup(customGroups[0].id));
    }
  } catch (error) {
    dispatch({ type: FETCH_CUSTOM_GROUPS_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const fetchGroupItems = () => async dispatch => {
  dispatch({ type: FETCH_CUSTOM_GROUP_ITEMS });

  try {
    const responses = await Promise.all([
      fetchGroupItemsApi(CUSTOM_GROUP_TYPES.STANDARD),
      fetchGroupItemsApi(CUSTOM_GROUP_TYPES.BATCH_REPORT),
    ]);

    return dispatch({
      type: FETCH_CUSTOM_GROUP_ITEMS_SUCCESS,
      customGroupItems: {
        CG: responses[0],
        BR: responses[1],
      },
    });
  } catch (error) {
    dispatch({ type: FETCH_CUSTOM_GROUP_ITEMS_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const editCustomGroup = group => async dispatch => {
  dispatch({ type: EDIT_CUSTOM_GROUP });
  try {
    const { groupKeys } = await editCustomGroupApi(group);
    const updatedGroup = groupKeys[0];
    if (updatedGroup) {
      dispatch(selectRow({
        name: agGridConfig.adminCustomGroups.name,
        fields: agGridConfig.adminCustomGroups.fields,
        data: { id: updatedGroup.groupId },
      }));
      dispatch(selectCustomGroup(updatedGroup.groupId));
    }
    await dispatch(fetchCustomGroups());
    dispatch(successNotification('Custom group updated successfully'));
    return dispatch({ type: EDIT_CUSTOM_GROUP_SUCCESS });
  } catch (error) {
    dispatch({ type: EDIT_CUSTOM_GROUP_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const saveNewCustomGroup = group => async dispatch => {
  dispatch({ type: SAVE_CUSTOM_GROUP });
  try {
    const { groupKeys } = await saveNewCustomGroupApi(group);
    const updatedGroup = groupKeys[0];
    if (updatedGroup) {
      dispatch(selectRow({
        name: agGridConfig.adminCustomGroups.name,
        fields: agGridConfig.adminCustomGroups.fields,
        data: { id: updatedGroup.groupId },
      }));
      dispatch(selectCustomGroup(updatedGroup.groupId));
    }

    await dispatch(fetchCustomGroups());
    dispatch(successNotification('Custom group created successfully'));
    dispatch({ type: CLEAR_CREATED_CUSTOM_GROUP });
    return dispatch({ type: SAVE_CUSTOM_GROUP_SUCCESS });
  } catch (error) {
    dispatch({ type: SAVE_CUSTOM_GROUP_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const removeCustomGroup = ({ groupId }) => async dispatch => {
  dispatch({ type: DELETE_CUSTOM_GROUP });
  try {
    await deleteCustomGroupApi({ groupId });
    await dispatch(fetchCustomGroups());
    dispatch(hideDeleteCustomGroupModal());
    dispatch(successNotification('Custom group has been deleted'));
    return dispatch({ type: DELETE_CUSTOM_GROUP_SUCCESS });
  } catch (error) {
    dispatch({ type: DELETE_CUSTOM_GROUP_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const fetchCustomGroupItemTypes = () => async dispatch => {
  dispatch({ type: FETCH_CUSTOM_GROUP_ITEM_TYPES });

  try {
    const standardGroupItemTypes = await fetchGroupItemTypesApi({ groupType: 'CG' });
    const batchReportGroupItemTypes = await fetchGroupItemTypesApi({ groupType: 'BR' });

    return dispatch({
      type: FETCH_CUSTOM_GROUP_ITEM_TYPES_SUCCESS,
      customGroupItemTypes: {
        CG: standardGroupItemTypes,
        BR: batchReportGroupItemTypes,
      },
    });
  } catch (error) {
    dispatch({ type: FETCH_CUSTOM_GROUP_ITEM_TYPES_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const createCustomGroup = () => async dispatch => dispatch({ type: CREATE_CUSTOM_GROUP });
export const enterEditMode = selectedCustomGroupId => async dispatch => dispatch({
  type: ENTER_CUSTOM_GROUP_EDIT_MODE,
  selectedCustomGroupId,
});
export const exitEditMode = () => async dispatch => dispatch({ type: EXIT_CUSTOM_GROUP_EDIT_MODE });
export const clearCreatedCustomGroup = () => async dispatch => dispatch({ type: CLEAR_CREATED_CUSTOM_GROUP });

export const refreshCustomGroupData = () => async dispatch => {
  dispatch({ type: REFRESH_CUSTOM_GROUP });
  try {
    await refreshCustomGroupDataApi();
    dispatch({ type: REFRESH_CUSTOM_GROUP_SUCCESS });
    dispatch(successNotification('Custom Group Data is being updated'));
  } catch (error) {
    if (error.isFetchingError) {
      dispatch({ type: REFRESH_CUSTOM_GROUP_FAILURE });
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};
